import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Stack,
  useMediaQuery,
} from "@mui/material";
import EmbraceLogo from "../Common/EmbraceLogo";
import { useAppDispatch, useAppSelector } from "../../Redux/hooks";
import {
  canNavigateForm,
  getFormData,
} from "../../Redux/actions/responseAction";
import { errorToastMessage, toastMessage } from "../../utils/toast";
import { useNavigate, useParams } from "react-router-dom";
import { useState } from "react";
import http from "../../utils/http";
import { Menu } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { formatScreeningFormCardDates } from "./screeningFormUtils";
import { setParticipantScreeningPhaseForm } from "../../Redux/reducers/responseSlice";

export const ScreeningHeader = ({
  languages,
  refreshPage,
}: {
  languages: any;
  refreshPage: () => void;
}) => {
  const isMobile = useMediaQuery("(max-width:900px)");
  const isFieldSubmitting = useAppSelector(
    (state) => state.response.fieldSubmitting,
  );
  const [showDrawer, setShowDrawer] = useState(false);

  return (
    <Stack
      direction={"row"}
      justifyContent={"space-between"}
      alignItems={"center"}
      sx={{
        height: "70px",
        px: 3,
        py: 2,
        border: { xs: "1px solid #E5E7EB", md: "none" },
        "& .header-logo": {
          maxWidth: "258px",
          maxHeight: "60px",
        },
        backgroundColor: "#FFF",
      }}
      gap={3}
    >
      {isMobile && languages.length > 1 && (
        <IconButton
          aria-label="menu"
          size="large"
          onClick={() => setShowDrawer(true)}
        >
          <Menu fontSize="inherit" />
        </IconButton>
      )}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          height: "100%",
        }}
      >
        <EmbraceLogo sx={{ height: { xs: 40, md: 44 } }} />
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          {isFieldSubmitting && <CircularProgress color="primary" size={24} />}
        </Box>
        <ScreenerActions refreshPage={refreshPage} />
      </Box>
    </Stack>
  );
};

type ConfirmProps = {
  show: boolean;
  onClose: () => void;
  studySlug?: string;
  formId?: string;
  refreshPage?: () => void;
};

const ConfirmModal = ({
  show,
  onClose,
  studySlug,
  formId,
  refreshPage,
}: ConfirmProps) => {
  const [submitLoader, setSubmitLoader] = useState(false);
  const { t } = useTranslation("participant-flow");
  const { id: studyId } = useParams();
  const dispatch = useAppDispatch();
  const attemptId = useAppSelector(
    (state) => state.response.screeningAttemptId,
  );
  const navigate = useNavigate();

  const navigateAfterScreening = () => {
    navigate(
      studySlug
        ? `/participant/thank-you?type=screening`
        : "/participant/thank-you",
      { replace: true },
    );
  };

  const storeEligibilityFromFinishAndNavigate = (
    finishRes: { data?: { data?: { eligibilityStatus?: string } } },
  ) => {
    const status = finishRes?.data?.data?.eligibilityStatus;
    if (status) {
      sessionStorage.setItem("screening-eligibility-status", status);
    }
    navigateAfterScreening();
  };

  const handleOk = async () => {
    try {
      setSubmitLoader(true);
      if (!formId) {
        toastMessage("warning", t("HEADER_REQUIRED_WARNING"));
        return;
      }
      const res = await http.post(
        `/study/${studyId}/participant-screening-responses/attempt/${attemptId}/form/${formId}/complete`,
      );
      const { nextForm, shouldFinalize } = res?.data?.data ?? {};
      if (shouldFinalize) {
        const finishRes = await http.post(
          `/study/${studyId}/participant-screening-responses/attempt/${attemptId}/finish`,
        );
        storeEligibilityFromFinishAndNavigate(finishRes);
      } else if (nextForm) {
        const selectedForm = formatScreeningFormCardDates(nextForm);
        sessionStorage.setItem("response-visit-form", selectedForm.id);
        dispatch(setParticipantScreeningPhaseForm({ form: selectedForm }));
        await dispatch(getFormData({ studyId, selectedForm }));
      } else if (refreshPage) {
        refreshPage();
      }
      onClose();
    } catch (err) {
      errorToastMessage(err as Error);
    } finally {
      setSubmitLoader(false);
    }
  };

  return (
    <Dialog
      open={show}
      onClose={onClose}
      PaperProps={{ sx: { padding: "8px" } }}
    >
      <DialogTitle>{t("HEADER_CONFIRM_TITLE")}</DialogTitle>
      <DialogContent>
        <DialogContentText>{t("HEADER_CONFIRM_BODY")}</DialogContentText>
      </DialogContent>
      <DialogActions>
        {!submitLoader ? (
          <>
            <Button variant="outlined" onClick={onClose}>
              {t("common:CANCEL")}
            </Button>
            <Button variant="contained" onClick={handleOk} autoFocus>
              {t("common:CONFIRM")}
            </Button>
          </>
        ) : (
          <CircularProgress size={20} sx={{ mr: 1 }} />
        )}
      </DialogActions>
    </Dialog>
  );
};

export const ScreenerActions = ({
  refreshPage,
}: {
  refreshPage?: () => void;
} = {}) => {
  const dispatch = useAppDispatch();
  const [showModal, setShowModal] = useState(false);
  const isFormLoading = useAppSelector((state) => state.response.isLoading);
  const editable = useAppSelector((state) => state.response.editable);
  const isAdminAttempted = useAppSelector(
    (state) => state.response.isAdminAttempted,
  );
  const isFieldSubmitting = useAppSelector(
    (state) => state.response.fieldSubmitting,
  );
  const selectedForm = useAppSelector((state) => state.response.selectedForm);
  const { studySlug } = useParams<{ studySlug?: string }>();
  const { t } = useTranslation("participant-flow");

  const onClose = () => setShowModal(false);

  const handleAction = () => {
    if (isFieldSubmitting) return;
    const canNavigate = dispatch(canNavigateForm());
    if (!canNavigate) {
      toastMessage("warning", t("HEADER_REQUIRED_WARNING"));
      return;
    }
    setShowModal(true);
  };

  if (
    isFormLoading ||
    (!editable && isAdminAttempted) ||
    selectedForm?.isSubmitted ||
    selectedForm?.locked
  ) {
    return null;
  }

  return (
    <>
      <Button
        variant="contained"
        onClick={handleAction}
        disabled={!!isFieldSubmitting}
      >
        {t("HEADER_SUBMIT")}
      </Button>
      {showModal && (
        <ConfirmModal
          show={showModal}
          onClose={onClose}
          studySlug={studySlug}
          formId={selectedForm?.id}
          refreshPage={refreshPage}
        />
      )}
    </>
  );
};

export const ScreenerFinish = ScreenerActions;
